import {
  Cartesian3,
  Entity,
  Quaternion,
  defined,
  JulianDate,
  createPropertyDescriptor,
  Matrix4,
  HeadingPitchRoll,
  Transforms,
  PolygonHierarchy,
  Cartesian2,
  Matrix3,
  Property,
  Check,
  ConstantPositionProperty,
  ConstantProperty,
  Rectangle,
  Math as CesiumMath,
  CallbackProperty,
} from "@cesium/engine";
import Utils from "../basic/Utils";

/**
 * @private
 */
const SCALE_IDENTITY = Cartesian3.ONE;

/**
 * Property 값 가져오기
 * @private
 * @param {Property} property
 * @param {JulianDate} time
 */
const getPropertyValue = (property, time = new JulianDate()) => {
  let result;
  if (defined(property)) {
    if (defined(property.getValue)) {
      result = property.getValue(time);
    } else {
      result = property;
    }
  }

  return result;
};

// /**
//  * @private
//  * @param {Cartesian3} origin 기준점
//  * @param {Cartesian3} translation 이동량
//  * @param {Quaternion | HeadingPitchRoll} rotation 회전량
//  * @param {Cartesian3} scale 스케일
//  */
// const computeMatrix = (origin, translation, rotation, scale) => {
//   // HeadingPitchRoll  => Quaternion
//   if (rotation instanceof HeadingPitchRoll) {
//     rotation = Quaternion.fromHeadingPitchRoll(rotation, new Quaternion());
//   }

//   //# ENU -> ECEF convert value
//   const toGlobal = Transforms.eastNorthUpToFixedFrame(origin);
//   const inverse = Matrix4.inverse(toGlobal, new Matrix4());

//   //# combine matrix => scale x rotation
//   const matrix = Matrix4.fromTranslationQuaternionRotationScale(
//     Cartesian3.ZERO,
//     rotation,
//     scale,
//     new Matrix4()
//   );

//   //# compute transform
//   const transform = Matrix4.multiply(
//     toGlobal,
//     Matrix4.multiply(matrix, inverse, new Matrix4()),
//     new Matrix4()
//   );

//   //# set translation
//   Matrix4.setTranslation(
//     transform,
//     new Cartesian3(
//       transform[12] + translation.x,
//       transform[13] + translation.y,
//       transform[14] + translation.z
//     ),
//     transform
//   );

//   return transform;
// };

/**
 * Cesium Entity computeModelMatrix 오버라이딩 함수
 * @private
 * @param {DitapEntity} entity
 * @param {JulianDate} time
 * @param {Matrix4} result
 * @returns {Matrix4 | undefined}
 */
const ditapEntityComputeModelMatrix = (entity, time, result) => {
  //>>includeStart('debug', pragmas.debug);
  Check.typeOf.object("time", time);
  //>>includeEnd('debug');
  const position = Property.getValueOrUndefined(
    entity._position,
    time,
    new Cartesian3()
  );
  if (!defined(position)) {
    return undefined;
  }

  const orientation = Property.getValueOrUndefined(
    entity._orientation,
    time,
    new Quaternion()
  );

  let scale;
  if (defined(entity._scale)) {
    if (defined(entity._scale.getValue)) {
      scale = entity._scale.getValue(time, new Cartesian3());
    } else {
      scale = entity._scale;
    }
  }
  scale = defined(scale) ? scale : new Cartesian3(1, 1, 1);

  if (!defined(orientation)) {
    result = Transforms.eastNorthUpToFixedFrame(position, undefined, result);
  } else {
    result = Matrix4.fromRotationTranslation(
      Matrix3.fromQuaternion(orientation, new Matrix3()),
      position,
      result
    );
  }
  Matrix4.setScale(result, scale, result);

  return result;
};

/**
 * Cesium Entity computeModelMatrixForHeightReference 오버라이딩 함수
 * ※ 안씀
 * @private
 */
// const ditapEntityComputeModelMatrixForHeightReference = (
//   entity,
//   time,
//   heightReferenceProperty,
//   heightOffset,
//   ellipsoid,
//   result
// ) => {
//   //>>includeStart('debug', pragmas.debug);
//   Check.typeOf.object("time", time);
//   //>>includeEnd('debug');
//   const heightReference = Property.getValueOrDefault(
//     heightReferenceProperty,
//     time,
//     HeightReference.NONE
//   );
//   let position = Property.getValueOrUndefined(
//     entity._position,
//     time,
//     new Cartesian3()
//   );
//   if (
//     heightReference === HeightReference.NONE ||
//     !defined(position) ||
//     Cartesian3.equalsEpsilon(position, Cartesian3.ZERO, CesiumMath.EPSILON8)
//   ) {
//     return entity.computeModelMatrix(time, result);
//   }

//   const carto = ellipsoid.cartesianToCartographic(position, new Cartographic());
//   if (heightReference === HeightReference.CLAMP_TO_GROUND) {
//     carto.height = heightOffset;
//   } else {
//     carto.height += heightOffset;
//   }
//   position = ellipsoid.cartographicToCartesian(carto, position);

//   const orientation = Property.getValueOrUndefined(
//     entity._orientation,
//     time,
//     new Quaternion()
//   );

//   let scale;
//   if (defined(entity._scale)) {
//     if (defined(entity._scale.getValue)) {
//       scale = entity._scale.getValue(time, new Cartesian3());
//     } else {
//       scale = entity._scale;
//     }
//   }
//   scale = defined(scale) ? scale : new Cartesian3(1, 1, 1);

//   if (!defined(orientation)) {
//     result = Transforms.eastNorthUpToFixedFrame(position, undefined, result);
//   } else {
//     result = Matrix4.fromRotationTranslation(
//       Matrix3.fromQuaternion(orientation, new Matrix3()),
//       position,
//       result
//     );
//   }
//   Matrix4.setScale(result, scale, result);

//   return result;
// };

/**
 * @private
 * @param {Entity} entity
 * @returns {boolean} isCallback
 */
const checkIsCallback = (entity) => {
  let isCallback = false;
  if (
    entity.position instanceof CallbackProperty ||
    entity.orientation instanceof CallbackProperty ||
    entity.scale instanceof CallbackProperty
  ) {
    isCallback = true;
  }

  return isCallback;
};

/**
 * Cartesian3 배열에 Matrix4 적용
 * @private
 * @param {Cartesian3[]} positions
 * @param {Matrix4} matrix
 * @param {Matrix4} inverse
 * @returns {Cartesian3[]}
 */
const setMatrixToPositionArray = (positions, matrix, inverse) => {
  const newPositions = [];

  // 회전행렬 적용
  for (const position of positions) {
    const newPosition = position.clone(new Cartesian3());

    // 역행렬
    if (defined(inverse)) {
      Matrix4.multiplyByPoint(inverse, newPosition, newPosition);
    }

    // 회전행렬
    Matrix4.multiplyByPoint(matrix, newPosition, newPosition);

    newPositions.push(newPosition);
  }

  return newPositions;
};

/**
 * Geometry Transform
 * @private
 * @param {Cartesian3[]} positions 좌표배열
 * @param {Cartesian3} origin 원본점
 * @param {Cartesian3} newPosition 이동점
 * @param {Quaternion} oldOrientaion 회전원본
 * @param {Quaternion} newOrientaion 적용회전
 * @param {Cartesian3} oldScale 비율원본
 * @param {Cartesian3} newScale 적용비율
 * @returns {Cartesian3[]} 변환좌표배열
 */
const transformGeometry = (
  positions,
  origin,
  newPosition,
  oldOrientaion,
  newOrientaion,
  oldScale,
  newScale
) => {
  // wall 변환행렬
  const matrix = Matrix4.fromTranslationQuaternionRotationScale(
    newPosition,
    newOrientaion,
    newScale,
    new Matrix4()
  );

  // wall 역행렬
  const inverse = Matrix4.inverse(
    Matrix4.fromTranslationQuaternionRotationScale(
      origin,
      oldOrientaion,
      oldScale,
      new Matrix4()
    ),
    new Matrix4()
  );

  const newPositions = setMatrixToPositionArray(positions, matrix, inverse);

  return newPositions;
};

/**
 * Geometry Transform CallbackProperty
 * @private
 * @param {Cartesian3[]} positions
 * @param {Cartesian3} origin
 * @param {Property} newPosition
 * @param {Quaternion} oldOrientaion
 * @param {Property} newOrientaion
 * @param {Cartesian3} oldScale
 * @param {Property} newScale
 * @returns {CallbackProperty}
 */
const createGeometryCallbackProperty = (
  positions,
  origin,
  newPosition,
  oldOrientaion,
  newOrientaion,
  oldScale,
  newScale
) => {
  const callbackProperty = new CallbackProperty((time) => {
    const _oldOrientaion =
      oldOrientaion ||
      Transforms.headingPitchRollQuaternion(
        origin,
        new HeadingPitchRoll(0, 0, 0)
      );
    const _oldScale = oldScale || SCALE_IDENTITY;
    const _newPosition = getPropertyValue(newPosition, time);
    const _newOrientaion =
      getPropertyValue(newOrientaion, time) ||
      Transforms.headingPitchRollQuaternion(
        _newPosition,
        new HeadingPitchRoll(0, 0, 0)
      );
    const _newScale = getPropertyValue(newScale, time) || SCALE_IDENTITY;

    // Transform Geometry
    const newPositions = transformGeometry(
      positions,
      origin,
      _newPosition,
      _oldOrientaion,
      _newOrientaion,
      _oldScale,
      _newScale
    );

    return newPositions;
  }, false);

  return callbackProperty;
};

/**
 *
 * @private
 * @param {PolygonHierarchy} hierarchy
 * @param {Cartesian3} origin
 * @param {Cartesian3} newPosition
 * @param {Quaternion} oldOrientaion
 * @param {Quaternion} newOrientaion
 * @param {Cartesian3} oldScale
 * @param {Cartesian3} newScale
 * @returns {PolygonHierarchy}
 */
const transformHierarchy = (
  hierarchy,
  origin,
  newPosition,
  oldOrientaion,
  newOrientaion,
  oldScale,
  newScale
) => {
  // wall 변환행렬
  const matrix = Matrix4.fromTranslationQuaternionRotationScale(
    newPosition,
    newOrientaion,
    newScale,
    new Matrix4()
  );

  // wall 역행렬
  const inverse = Matrix4.inverse(
    Matrix4.fromTranslationQuaternionRotationScale(
      origin,
      oldOrientaion,
      oldScale,
      new Matrix4()
    ),
    new Matrix4()
  );

  const newPositions = setMatrixToPositionArray(
    hierarchy.positions,
    matrix,
    inverse
  );
  const newHoles = setMatrixToPositionArray(hierarchy.holes, matrix, inverse);

  return new PolygonHierarchy(newPositions, newHoles);
};

/**
 * PolygonHieracchy CallbackPropety 생성 함수
 * @private
 * @param {PolygonHierarchy} hierarchy
 * @param {Cartesian3} origin
 * @param {Property} newPosition
 * @param {Quaternion} oldOrientaion
 * @param {Property} newOrientaion
 * @param {Cartesian3} oldScale
 * @param {Property} newScale
 * @returns {CallbackProperty}
 */
const createHierarchyCallbackProperty = (
  hierarchy,
  origin,
  newPosition,
  oldOrientaion,
  newOrientaion,
  oldScale,
  newScale
) => {
  const callbackProperty = new CallbackProperty((time) => {
    // 새로 입력된 Entity Position
    const _oldOrientaion =
      oldOrientaion ||
      Transforms.headingPitchRollQuaternion(
        origin,
        new HeadingPitchRoll(0, 0, 0)
      );
    const _oldScale = oldScale || SCALE_IDENTITY;
    const _newPosition = getPropertyValue(newPosition, time);
    const _newOrientaion =
      getPropertyValue(newOrientaion, time) ||
      Transforms.headingPitchRollQuaternion(
        _newPosition,
        new HeadingPitchRoll(0, 0, 0)
      );
    const _newScale = getPropertyValue(newScale, time) || SCALE_IDENTITY;

    const newHierarchy = transformHierarchy(
      hierarchy,
      origin,
      _newPosition,
      _oldOrientaion,
      _newOrientaion,
      _oldScale,
      _newScale
    );

    return newHierarchy;
  }, false);

  return callbackProperty;
};

/**
 * Geometry Transform
 * @private
 * @param {Rectangle} rectangle
 * @param {Cartesian3} origin 원본점
 * @param {Cartesian3} newPosition 이동점
 * @param {Quaternion} oldOrientaion 회전원본
 * @param {Quaternion} newOrientaion 적용회전
 * @param {Cartesian3} oldScale 비율원본
 * @param {Cartesian3} newScale 적용비율
 * @returns {Cartesian3[]} 변환좌표배열
 */
const transformRectangle = (
  rectangle,
  origin,
  newPosition,
  oldOrientaion,
  newOrientaion,
  oldScale,
  newScale
) => {
  // reactangle radians -> Cartesian3
  const east = CesiumMath.toDegrees(rectangle.east);
  const north = CesiumMath.toDegrees(rectangle.north);
  const south = CesiumMath.toDegrees(rectangle.south);
  const west = CesiumMath.toDegrees(rectangle.west);
  const positions = Cartesian3.fromDegreesArray([west, north, east, south]);

  // wall 변환행렬
  const matrix = Matrix4.fromTranslationQuaternionRotationScale(
    newPosition,
    newOrientaion,
    newScale,
    new Matrix4()
  );

  // wall 역행렬
  const inverse = Matrix4.inverse(
    Matrix4.fromTranslationQuaternionRotationScale(
      origin,
      oldOrientaion,
      oldScale,
      new Matrix4()
    ),
    new Matrix4()
  );

  const newPositions = setMatrixToPositionArray(positions, matrix, inverse);

  return Rectangle.fromCartesianArray(newPositions);
};

/**
 * Rectangle CallbackProperty 생성함수
 * @private
 * @param {Rectangle} rectangle
 * @param {Cartesian3} origin
 * @param {Property} newPosition
 * @param {Property} oldOrientaion
 * @param {Property} newOrientaion
 * @param {Property} oldScale
 * @param {Property} newScale
 * @returns {CallbackProperty}
 */
const createRectangleCallbackProperty = (
  rectangle,
  origin,
  newPosition,
  oldOrientaion,
  newOrientaion,
  oldScale,
  newScale
) => {
  const callbackProperty = new CallbackProperty((time) => {
    const _oldOrientaion =
      oldOrientaion ||
      Transforms.headingPitchRollQuaternion(
        origin,
        new HeadingPitchRoll(0, 0, 0)
      );
    const _oldScale = oldScale || SCALE_IDENTITY;
    const _newPosition = getPropertyValue(newPosition, time);
    const _newOrientaion =
      getPropertyValue(newOrientaion, time) ||
      Transforms.headingPitchRollQuaternion(
        _newPosition,
        new HeadingPitchRoll(0, 0, 0)
      );
    const _newScale = getPropertyValue(newScale, time) || SCALE_IDENTITY;

    // Transform Geometry
    const newReactangle = transformRectangle(
      rectangle,
      origin,
      _newPosition,
      _oldOrientaion,
      _newOrientaion,
      _oldScale,
      _newScale
    );

    return newReactangle;
  }, false);

  return callbackProperty;
};

// /**
//  * @private
//  * @param {*} positions
//  * @param {*} origin
//  * @param {*} newPosition
//  * @param {*} oldOrientation
//  * @param {*} newOrientation
//  * @param {*} oldScale
//  * @param {*} newScale
//  * @returns
//  */
// const createWallCallbackProperty = (positions, origin, newPosition, oldOrientation, newOrientation, oldScale, newScale) => {
//   const callbackProperty = new CallbackProperty((time) => {
//     const _newPosition = getPropertyValue(newPosition, time);
//     const _newOrientation = getPropertyValue(newOrientation, time) || Transforms.headingPitchRollQuaternion(newPosition, new HeadingPitchRoll(0,0,0));
//     const _newScale = getPropertyValue(newScale, time) || new Cartesian3(1,1,1);

//     // wall 변환행렬
//     const wallMatrix = Matrix4.fromTranslationQuaternionRotationScale(
//       _newPosition,
//       _newOrientation,
//       _newScale,
//       new Matrix4()
//     );

//     // wall 역행렬
//     const wallInverse = Matrix4.inverse(
//       Matrix4.fromTranslationQuaternionRotationScale(
//         _newPosition,
//         _newOrientation,
//         _oldValue,
//         new Matrix4()
//       ),
//       new Matrix4()
//     );

//     const newPositions = setMatrixToPositionArray(
//       positions,
//       wallMatrix,
//       wallInverse
//     );

//     return newPositions;
//   }, false);

//   return callbackProperty;
// };

// /**
//  * Positions CallbackProperty 생성함수
//  * @private
//  * @param {Cartesian3[]} positions
//  * @param {Cartesian3} origin
//  * @param {Property} newPosition
//  * @param {Property} oldOrientation
//  * @param {Property} newOrientation
//  * @param {Property} oldScale
//  * @param {Property} newScale
//  * @returns {CallbackProperty}
//  */
// const createPositionsCallbackProperty = (
//   positions,
//   origin,
//   newPosition,
//   oldOrientation,
//   newOrientation,
//   oldScale,
//   newScale
// ) => {
//   const callbackProperty = new CallbackProperty((time) => {
//     // 새로 입력된 Entity Position
//     const _oldOrientation =
//       getPropertyValue(oldOrientation, time) ||
//       Transforms.headingPitchRollQuaternion(
//         origin,
//         new HeadingPitchRoll(0, 0, 0)
//       );
//     const _oldScale =
//       getPropertyValue(oldScale, time) || new Cartesian3(1, 1, 1);
//     const _newPosition = getPropertyValue(newPosition, time);
//     const _newOrientaion =
//       getPropertyValue(newOrientation, time) ||
//       Transforms.headingPitchRollQuaternion(
//         _newPosition,
//         new HeadingPitchRoll(0, 0, 0)
//       );
//     const _newScale =
//       getPropertyValue(newScale, time) || new Cartesian3(1, 1, 1);

//     // 이동량: 최신값 - 콜백 최초 설정값
//     const translation = Cartesian3.subtract(
//       _newPosition,
//       origin,
//       new Cartesian3()
//     );

//     // 변환행렬
//     const matrix = computeMatrix(
//       origin,
//       translation,
//       _newOrientaion,
//       _newScale
//     );

//     // 역행렬
//     const inverse = Matrix4.inverse(
//       computeMatrix(origin, Cartesian3.ZERO, _oldOrientation, _oldScale),
//       new Matrix4()
//     );

//     const newPositions = setMatrixToPositionArray(positions, matrix, inverse);

//     return newPositions;
//   }, false);

//   return callbackProperty;
// };

/**
 * Entity 전체 모델 좌표 추출
 * @private
 * @param {Entity} entity
 * @returns {Cartesian3[]}
 */
const getEntityAllPositions = (entity) => {
  if (!(entity instanceof Entity)) {
    throw new Error("entity is invalid");
  }

  // 좌표 목록
  const entityPosition = getPropertyValue(entity.position);
  let totalPositions = defined(entityPosition) ? [entityPosition] : [];

  const corridor = entity.corridor;
  if (defined(corridor)) {
    //corridor.positions
    const positions = getPropertyValue(corridor.positions);
    if (positions) {
      totalPositions = totalPositions.concat(positions);
    }
  }

  const polygon = entity.polygon;
  if (defined(polygon)) {
    //hierarchy
    const positions = getPropertyValue(polygon.hierarchy);
    if (positions) {
      totalPositions = totalPositions.concat(positions.positions);
    }
  }

  const polyline = entity.polyline;
  if (defined(polyline)) {
    //positions
    const positions = getPropertyValue(polyline.positions);
    if (positions) {
      totalPositions = totalPositions.concat(positions);
    }
  }

  const polylineVolume = entity.polylineVolume;
  if (defined(polylineVolume)) {
    //positions
    const positions = getPropertyValue(polylineVolume.positions);
    if (positions) {
      totalPositions = totalPositions.concat(positions);
    }
  }

  const rectangle = entity.rectangle;
  if (defined(rectangle)) {
    //coordinates
    const positions = getPropertyValue(rectangle.coordinates);
    if (positions) {
      //const cartographic = Rectangle.center(positions, new Cartographic());
      //const center = Cartographic.toCartesian(cartographic);

      // reactangle radians -> Cartesian3
      const east = CesiumMath.toDegrees(positions.east);
      const north = CesiumMath.toDegrees(positions.north);
      const south = CesiumMath.toDegrees(positions.south);
      const west = CesiumMath.toDegrees(positions.west);
      const cartesians = Cartesian3.fromDegreesArray([
        east,
        north,
        west,
        south,
      ]);

      totalPositions = totalPositions.concat(cartesians);
    }
  }

  //const tileset = entity.tileset;
  // if (defined(tileset)) {
  //     //tileset.boundingSphere.center
  //     //const position = tileset.boundingSphere.center;
  //     //totalPositions = totalPositions.concat(position);
  // }

  const wall = entity.wall;
  if (defined(wall)) {
    //positions
    const positions = getPropertyValue(wall.positions);
    if (positions) {
      totalPositions = totalPositions.concat(positions);
    }
  }

  return totalPositions;
};

/**
 * Entity 중심점 계산
 * @private
 * @param {Entity} entity
 * @returns {Cartesian3 | undefined}
 */
const getEntityPosition = (entity) => {
  if (!(entity instanceof Entity)) {
    throw Error("entity is invalid");
  }

  // Entity.position 값을 기준점으로 삼는다.
  let result;
  const entityPosition = getPropertyValue(entity.position);
  if (entityPosition) {
    result = entityPosition;

    // Entity.position이 없을 경우 모든 속성 탐색 후 중심점 계산.
  } else {
    // Entity 내부 모델 좌표 추출
    const positions = getEntityAllPositions(entity);

    // 중심점 계산
    let centerPosition;
    if (0 < positions.length) {
      const minmax = Utils.computeMinMaxCartesian(positions);

      // 중심점
      centerPosition = new Cartesian3(
        (minmax[0].x + minmax[1].x) / 2,
        (minmax[0].y + minmax[1].y) / 2,
        (minmax[0].z + minmax[1].z) / 2
      );
    }
    // if (0 < positions.length) {
    //   const tempPosition = new Cartesian3(0, 0, 0);
    //   for (const position of positions) {
    //     Cartesian3.add(tempPosition, position, tempPosition);
    //   }
    //   Cartesian3.divideByScalar(tempPosition, positions.length, tempPosition);

    //   centerPosition = tempPosition;
    // }

    result = centerPosition;
  }

  return result;
};

/**
 *
 * @private
 * @param {Entity} entity
 * @param {Cartesian3} origin
 * @param {Cartesian3} newValue
 */
const setEntityPosition = (entity, origin, newValue) => {
  const isCallback = checkIsCallback(entity);
  const oldOrientaion =
    getPropertyValue(entity.orientation) ||
    Transforms.headingPitchRollQuaternion(
      origin,
      new HeadingPitchRoll(0, 0, 0)
    );
  const oldScale = getPropertyValue(entity.scale) || SCALE_IDENTITY;

  // billboard => Entity.position 대체

  // box => Entity.position 대체

  // corridor
  const corridor = entity._corridor;
  if (defined(corridor)) {
    const positions = getPropertyValue(corridor.positions);
    if (isCallback) {
      corridor._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        oldOrientaion,
        entity.orientation,
        oldScale,
        entity.scale
      );
    } else {
      const newPositions = transformGeometry(
        positions,
        origin,
        newValue,
        oldOrientaion,
        oldOrientaion,
        oldScale,
        oldScale
      );
      corridor._positions = new ConstantProperty(newPositions);
    }
    corridor._definitionChanged.raiseEvent(
      entity,
      "corridor",
      corridor,
      corridor
    );
  }

  // cylinder => Entity.position 대체

  // ellipse => Entity.position 대체

  // ellipsoid => Entity.position 대체

  // label => Entity.position 대체

  // model => Entity.position 대체

  // plane => Entity.position 대체

  // point => Entity.position 대체

  // polygon
  const polygon = entity._polygon;
  if (defined(polygon)) {
    //hierarchy
    const positions = getPropertyValue(polygon.hierarchy);
    // CallbackProperty
    if (isCallback) {
      polygon._hierarchy = createHierarchyCallbackProperty(
        positions,
        origin,
        entity.position,
        oldOrientaion,
        entity.orientation,
        oldScale,
        entity.scale
      );
      // ConstantProperty
    } else {
      const newPositions = transformHierarchy(
        positions,
        origin,
        newValue,
        oldOrientaion,
        oldOrientaion,
        oldScale,
        oldScale
      );
      polygon._hierarchy = new ConstantProperty(newPositions);
    }
    polygon._definitionChanged.raiseEvent(entity, "polygon", polygon, polygon);
  }

  // polyline
  const polyline = entity._polyline;
  if (defined(polyline)) {
    //positions
    const positions = getPropertyValue(polyline.positions);
    // CallbackProperty
    if (isCallback) {
      polyline._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        oldOrientaion,
        entity.orientation,
        oldScale,
        entity.scale
      );
    } else {
      const newPositions = transformGeometry(
        positions,
        origin,
        newValue,
        oldOrientaion,
        oldOrientaion,
        oldScale,
        oldScale
      );
      polyline._positions = new ConstantProperty(newPositions);
    }
    polyline._definitionChanged.raiseEvent(
      entity,
      "polyline",
      polyline,
      polyline
    );
  }

  // polylineVolume
  const polylineVolume = entity._polylineVolume;
  if (defined(polylineVolume)) {
    //positions
    const positions = getPropertyValue(polylineVolume.positions);
    if (isCallback) {
      polylineVolume._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        oldOrientaion,
        entity.orientation,
        oldScale,
        entity.scale
      );
    } else {
      const newPositions = transformGeometry(
        positions,
        origin,
        newValue,
        oldOrientaion,
        oldOrientaion,
        oldScale,
        oldScale
      );
      polylineVolume._positions = new ConstantProperty(newPositions);
    }
    polylineVolume._definitionChanged.raiseEvent(
      entity,
      "polylineVolume",
      polylineVolume,
      polylineVolume
    );
  }

  // rectangle
  const rectangle = entity._rectangle;
  if (defined(rectangle)) {
    //coordinates
    const positions = getPropertyValue(rectangle.coordinates);
    // CallbackProperty
    if (isCallback) {
      rectangle._coordinates = createRectangleCallbackProperty(
        positions,
        origin,
        entity.position,
        oldOrientaion,
        entity.orientation,
        oldScale,
        entity.scale
      );
      // ConstantProperty
    } else {
      // reactangle radians -> Cartesian3
      const newPositions = transformRectangle(
        positions,
        origin,
        newValue,
        oldOrientaion,
        oldOrientaion,
        oldScale,
        oldScale
      );
      rectangle._coordinates = new ConstantProperty(newPositions);
    }

    // Geometry 내부 프로퍼티 값 변경 시 raiseEvent를 해줘야함.
    rectangle.definitionChanged.raiseEvent(
      entity,
      "rectangle",
      rectangle,
      rectangle
    );
  }

  // tileset => entity.position 대체

  // wall
  const wall = entity._wall;
  if (defined(wall)) {
    const positions = getPropertyValue(wall.positions);
    if (isCallback) {
      wall._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        oldOrientaion,
        entity.orientation,
        oldScale,
        entity.scale
      );
    } else {
      const newPositions = transformGeometry(
        positions,
        origin,
        newValue,
        oldOrientaion,
        oldOrientaion,
        oldScale,
        oldScale
      );
      wall._positions = new ConstantProperty(newPositions);
    }
    wall.definitionChanged.raiseEvent(entity, "wall", wall, wall);
  }
};

/**
 * @private
 * @param {Entity} entity
 * @param {Cartesian3} origin
 * @param {Quaternion} oldOrientation
 * @param {Quaternion} newOrientaion
 */
const setEntityRotation = (entity, origin, newValue, oldValue) => {
  const isCallback = checkIsCallback(entity);
  const oldScale = getPropertyValue(entity.scale) || SCALE_IDENTITY;

  // 회전축
  const axis = Quaternion.computeAxis(newValue, new Cartesian3());
  // 회전각
  const angle = Quaternion.computeAngle(newValue, new Cartesian3());

  // billboard
  const billboard = entity._billboard;
  if (defined(billboard)) {
    if (isCallback) {
      // 단위 radian
      billboard.alignedAxis = new CallbackProperty((time) => {
        const orientation = getPropertyValue(entity.orientation, time);
        // 회전축
        const axis = Quaternion.computeAxis(orientation, new Cartesian3());

        return axis;
      }, false);
      billboard.rotation = new CallbackProperty((time) => {
        const orientation = getPropertyValue(entity.orientation, time);
        // 회전각
        const angle = Quaternion.computeAngle(orientation, new Cartesian3());

        return angle;
      }, false);
    } else {
      // 단위 radian
      billboard.alignedAxis = axis;
      billboard.rotation = angle;
    }
  }

  // box => Entity.orientation 대체

  // corridor
  const corridor = entity._corridor;
  if (defined(corridor)) {
    //corridor.positions
    const positions = getPropertyValue(corridor.positions);
    if (isCallback) {
      corridor._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        oldValue,
        entity.orientation,
        oldScale,
        entity.scale
      );
    } else {
      const newPositions = transformGeometry(
        positions,
        origin,
        origin,
        oldValue,
        newValue,
        oldScale,
        oldScale
      );
      corridor._positions = new ConstantProperty(newPositions);
    }
    corridor._definitionChanged.raiseEvent(
      entity,
      "corridor",
      corridor,
      corridor
    );
  }

  // cylinder => Entity.orientation 대체

  // ellipse
  const ellipse = entity._ellipse;
  if (defined(ellipse)) {
    if (isCallback) {
      ellipse.rotation = new CallbackProperty((time) => {
        const orientation = getPropertyValue(entity.orientation, time);
        // 회전각
        const angle = Quaternion.computeAngle(orientation, new Cartesian3());

        return angle;
      }, false);
    } else {
      ellipse.rotation = angle;
    }
  }

  // ellipsoid => Entity.orientation 대체

  // label => 기능 미지원

  // model => Entity.orientation 대체

  // plane => Entity.orientation 대체

  // point => Entity.orientation 대체

  // polygon
  const polygon = entity._polygon;
  if (defined(polygon)) {
    //hierarchy
    const positions = getPropertyValue(polygon.hierarchy);
    // CallbackProperty
    if (isCallback) {
      polygon._hierarchy = createHierarchyCallbackProperty(
        positions,
        origin,
        entity.position,
        oldValue,
        entity.orientation,
        oldScale,
        entity.scale
      );
      // ConstantProperty
    } else {
      const newPositions = transformHierarchy(
        positions,
        origin,
        origin,
        oldValue,
        newValue,
        oldScale,
        oldScale
      );
      polygon._hierarchy = new ConstantProperty(newPositions);
    }
    polygon._definitionChanged.raiseEvent(entity, "polygon", polygon, polygon);
  }

  // polyline
  const polyline = entity._polyline;
  if (defined(polyline)) {
    //positions
    const positions = getPropertyValue(polyline.positions);
    if (isCallback) {
      polyline._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        oldValue,
        entity.orientation,
        oldScale,
        entity.scale
      );
    } else {
      const newPositions = transformGeometry(
        positions,
        origin,
        origin,
        oldValue,
        newValue,
        oldScale,
        oldScale
      );
      polyline._positions = new ConstantProperty(newPositions);
    }
    polyline._definitionChanged.raiseEvent(
      entity,
      "polyline",
      polyline,
      polyline
    );
  }

  // polylineVolume
  const polylineVolume = entity._polylineVolume;
  if (defined(polylineVolume)) {
    //positions
    const positions = getPropertyValue(polylineVolume.positions);
    if (isCallback) {
      polylineVolume._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        oldValue,
        entity.orientation,
        oldScale,
        entity.scale
      );
    } else {
      const newPositions = transformGeometry(
        positions,
        origin,
        origin,
        oldValue,
        newValue,
        oldScale,
        oldScale
      );
      polylineVolume._positions = new ConstantProperty(newPositions);
    }
    polylineVolume._definitionChanged.raiseEvent(
      entity,
      "polylineVolume",
      polylineVolume,
      polylineVolume
    );
  }

  // rectangle
  const rectangle = entity._rectangle;
  if (defined(rectangle)) {
    //coordinates
    if (isCallback) {
      rectangle.rotation = new CallbackProperty(() => {
        const newOrientaion = getPropertyValue(entity.orientation);
        const headingPitchRoll = HeadingPitchRoll.fromQuaternion(
          newOrientaion,
          new HeadingPitchRoll()
        );

        return headingPitchRoll.heading;
      }, false);
    } else {
      const headingPitchRoll = HeadingPitchRoll.fromQuaternion(
        newValue,
        new HeadingPitchRoll()
      );
      rectangle.rotation = headingPitchRoll.heading;
    }
  }

  // tileset => computeModelMatrix

  // wall
  const wall = entity._wall;
  if (defined(wall)) {
    const positions = getPropertyValue(wall.positions);
    if (isCallback) {
      wall._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        oldValue,
        entity.orientation,
        oldScale,
        entity.scale
      );
    } else {
      const newPositions = transformGeometry(
        positions,
        origin,
        origin,
        oldValue,
        newValue,
        oldScale,
        oldScale
      );
      wall._positions = new ConstantProperty(newPositions);
    }
    wall.definitionChanged.raiseEvent(entity, "wall", wall, wall);
  }
};

/**
 *
 * @private
 * @param {DitapEntity} entity
 * @param {Cartesian3} newValue
 * @param {Cartesian3} oldValue
 * @param {Matrix4} matrix
 * @param {Matrix4} inverse
 */
const setEntityScale = (entity, origin, newValue, oldValue) => {
  const isCallback = checkIsCallback(entity);
  const oldOrientation =
    getPropertyValue(entity.orientation) ||
    Transforms.headingPitchRollQuaternion(
      origin,
      new HeadingPitchRoll(0, 0, 0)
    );
  const oldMaximum = Cartesian3.maximumComponent(oldValue);
  const newMaximum = Cartesian3.maximumComponent(newValue);

  // billboard
  const billboard = entity._billboard;
  if (defined(billboard)) {
    if (entity.scale instanceof CallbackProperty) {
      billboard.scale = new CallbackProperty((time) => {
        const newScale = getPropertyValue(entity.scale, time);
        const newMaximum = Cartesian3.maximumComponent(newScale);

        return newMaximum;
      }, false);
    } else {
      billboard.scale = newMaximum;
    }
  }

  // box => computeModelMatrixForHeightReference 대체
  const box = entity._box;
  if (defined(box)) {
    const dimentions = getPropertyValue(box.dimensions);
    if (entity.scale instanceof CallbackProperty) {
      box.dimensions = new CallbackProperty((time) => {
        const newScale = getPropertyValue(entity.scale, time);

        const x = (dimentions.x / oldValue.x) * newScale.x;
        const y = (dimentions.y / oldValue.y) * newScale.y;
        const z = (dimentions.z / oldValue.z) * newScale.z;

        return new Cartesian3(x < 0 ? 1 : x, y < 0 ? 1 : y, z < 0 ? 1 : z);
      }, false);
    } else {
      const x = (dimentions.x / oldValue.x) * newValue.x;
      const y = (dimentions.y / oldValue.y) * newValue.y;
      const z = (dimentions.z / oldValue.z) * newValue.z;

      box.dimensions = new Cartesian3(
        x < 0 ? 1 : x,
        y < 0 ? 1 : y,
        z < 0 ? 1 : z
      );
    }
  }

  // corridor
  const corridor = entity._corridor;
  if (defined(corridor)) {
    const positions = getPropertyValue(corridor.positions);
    if (isCallback) {
      corridor._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        oldOrientation,
        entity.orientation,
        oldValue,
        entity.scale
      );
    } else {
      const newPositions = transformGeometry(
        positions,
        origin,
        origin,
        oldOrientation,
        oldOrientation,
        oldValue,
        newValue
      );
      corridor._positions = new ConstantProperty(newPositions);
    }
    corridor._definitionChanged.raiseEvent(
      entity,
      "corridor",
      corridor,
      corridor
    );
  }

  // cylinder => computeModelMatrixForHeightReference 대체
  const cylinder = entity._cylinder;
  if (defined(cylinder)) {
    const bottomRadius = getPropertyValue(cylinder.bottomRadius);
    const topRadius = getPropertyValue(cylinder.topRadius);
    const length = getPropertyValue(cylinder.length);
    if (entity.scale instanceof CallbackProperty) {
      cylinder.bottomRadius = new CallbackProperty((time) => {
        const newScale = getPropertyValue(entity.scale, time);
        const newMaximum = Cartesian3.maximumComponent(newScale);

        return (bottomRadius / oldMaximum) * newMaximum;
      }, false);
      cylinder.topRadius = new CallbackProperty((time) => {
        const newScale = getPropertyValue(entity.scale, time);
        const newMaximum = Cartesian3.maximumComponent(newScale);

        return (topRadius / oldMaximum) * newMaximum;
      }, false);
      cylinder.length = new CallbackProperty((time) => {
        const newScale = getPropertyValue(entity.scale, time);
        const newMaximum = Cartesian3.maximumComponent(newScale);

        return (length / oldMaximum) * newMaximum;
      }, false);
    } else {
      cylinder.bottomRadius = (bottomRadius / oldMaximum) * newMaximum;
      cylinder.topRadius = (topRadius / oldMaximum) * newMaximum;
      cylinder.length = (length / oldMaximum) * newMaximum;
    }
  }

  // ellipse
  const ellipse = entity._ellipse;
  if (defined(ellipse)) {
    const semiMajorAxis = getPropertyValue(ellipse.semiMajorAxis);
    const semiMinorAxis = getPropertyValue(ellipse.semiMinorAxis);
    if (entity.scale instanceof CallbackProperty) {
      ellipse.semiMajorAxis = new CallbackProperty((time) => {
        const newScale = getPropertyValue(entity.scale, time);

        const newSemiMajorAxis = (semiMajorAxis / oldValue.x) * newScale.x;
        const newSemiMinorAxis = (semiMinorAxis / oldValue.y) * newScale.y;

        return newSemiMajorAxis < newSemiMinorAxis
          ? newSemiMinorAxis
          : newSemiMajorAxis;
      }, false);
      ellipse.semiMinorAxis = new CallbackProperty((time) => {
        const newScale = getPropertyValue(entity.scale, time);

        const newSemiMajorAxis = (semiMajorAxis / oldValue.x) * newScale.x;
        const newSemiMinorAxis = (semiMinorAxis / oldValue.y) * newScale.y;

        return newSemiMinorAxis < newSemiMajorAxis
          ? newSemiMinorAxis
          : newSemiMajorAxis;
      }, false);
    } else {
      const newSemiMajorAxis = (semiMajorAxis / oldValue.x) * newValue.x;
      const newSemiMinorAxis = (semiMinorAxis / oldValue.y) * newValue.y;
      ellipse.semiMajorAxis = new ConstantProperty(
        newSemiMajorAxis < newSemiMinorAxis
          ? newSemiMinorAxis
          : newSemiMajorAxis
      );
      ellipse.semiMinorAxis = new ConstantProperty(
        newSemiMinorAxis < newSemiMajorAxis
          ? newSemiMinorAxis
          : newSemiMajorAxis
      );
    }
  }

  // ellipsoid => computeModelMatrixForHeightReference 대체
  const ellipsoid = entity._ellipsoid;
  if (defined(ellipsoid)) {
    const radii = getPropertyValue(ellipsoid.radii);
    if (entity.scale instanceof CallbackProperty) {
      ellipsoid.radii = new CallbackProperty((time) => {
        const newScale = getPropertyValue(entity.scale, time);

        return new Cartesian3(
          (radii.x / oldValue.x) * newScale.x,
          (radii.y / oldValue.y) * newScale.y,
          (radii.z / oldValue.z) * newScale.z
        );
      }, false);
    } else {
      ellipsoid.radii = new Cartesian3(
        (radii.x / oldValue.x) * newValue.x,
        (radii.y / oldValue.y) * newValue.y,
        (radii.z / oldValue.z) * newValue.z
      );
    }
  }

  // label
  const label = entity._label;
  if (defined(label)) {
    const labelScale = getPropertyValue(label.scale) || 1;
    if (entity.scale instanceof CallbackProperty) {
      label.scale = new CallbackProperty((time) => {
        const newScale = getPropertyValue(entity.scale, time);
        const newMaximum = Cartesian3.maximumComponent(newScale);

        return (labelScale / oldMaximum) * newMaximum;
      }, false);
    } else {
      label.scale = (labelScale / oldMaximum) * newMaximum;
    }
  }

  // model => Entity.scale 대체

  // plane
  const plane = entity._plane;
  if (defined(plane)) {
    if (entity.scale instanceof CallbackProperty) {
      const dimensions = getPropertyValue(plane.dimensions);
      plane.dimensions = new CallbackProperty((time) => {
        const newScale = getPropertyValue(entity.scale, time);

        return new Cartesian2(
          (dimensions.x / oldValue.x) * newScale.x,
          (dimensions.y / oldValue.y) * newScale.y
        );
      }, false);
    } else {
      const dimensions = getPropertyValue(plane.dimensions);
      plane.dimensions = new Cartesian2(
        (dimensions.x / oldValue.x) * newValue.x,
        (dimensions.y / oldValue.y) * newValue.y
      );
    }
  }

  // point
  const point = entity._point;
  if (defined(point)) {
    const pixelSize = getPropertyValue(point.pixelSize) || 1;
    if (entity.scale instanceof CallbackProperty) {
      point.pixelSize = new CallbackProperty((time) => {
        const newScale = getPropertyValue(entity.scale, time);
        const newMaximum = Cartesian3.maximumComponent(newScale);

        return (pixelSize / oldMaximum) * newMaximum;
      }, false);
    } else {
      point.pixelSize = (pixelSize / oldMaximum) * newMaximum;
    }
  }

  // polygon
  const polygon = entity._polygon;
  if (defined(polygon)) {
    const positions = getPropertyValue(polygon.hierarchy);
    if (isCallback) {
      polygon._hierarchy = createHierarchyCallbackProperty(
        positions,
        origin,
        entity.position,
        oldOrientation,
        entity.orientation,
        oldValue,
        entity.scale
      );
    } else {
      const newPositions = transformHierarchy(
        positions,
        origin,
        origin,
        oldOrientation,
        oldOrientation,
        oldValue,
        newValue
      );
      polygon._hierarchy = new ConstantProperty(newPositions);
    }
    polygon._definitionChanged.raiseEvent(entity, "polygon", polygon, polygon);
  }

  // polyline
  const polyline = entity._polyline;
  if (defined(polyline)) {
    const positions = getPropertyValue(polyline.positions);
    if (isCallback) {
      polyline._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        oldOrientation,
        entity.orientation,
        oldValue,
        entity.scale
      );
    } else {
      const newPositions = transformGeometry(
        positions,
        origin,
        origin,
        oldOrientation,
        oldOrientation,
        oldValue,
        newValue
      );
      polyline._positions = new ConstantProperty(newPositions);
    }
    polyline._definitionChanged.raiseEvent(
      entity,
      "polyline",
      polyline,
      polyline
    );
  }

  // polylineVolume
  const polylineVolume = entity._polylineVolume;
  if (defined(polylineVolume)) {
    const positions = getPropertyValue(polylineVolume.positions);
    const shape = getPropertyValue(polylineVolume.shape);
    if (isCallback) {
      polylineVolume._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        oldOrientation,
        entity.orientation,
        oldValue,
        entity.scale
      );
      polylineVolume._shape = createGeometryCallbackProperty(
        shape,
        origin,
        entity.position,
        oldOrientation,
        entity.orientation,
        oldValue,
        entity.scale
      );
    } else {
      const newPositions = transformGeometry(
        positions,
        origin,
        origin,
        oldOrientation,
        oldOrientation,
        oldValue,
        newValue
      );
      polylineVolume._positions = new ConstantProperty(newPositions);
      const newShape = transformGeometry(
        shape,
        origin,
        origin,
        oldOrientation,
        oldOrientation,
        oldValue,
        newValue
      );
      polylineVolume._shape = new ConstantProperty(newShape);
    }
    polylineVolume._definitionChanged.raiseEvent(
      entity,
      "polylineVolume",
      polylineVolume,
      polylineVolume
    );
  }

  // rectangle
  const rectangle = entity._rectangle;
  if (defined(rectangle)) {
    const positions = getPropertyValue(rectangle.coordinates);
    if (entity.scale instanceof CallbackProperty) {
      rectangle._coordinates = createRectangleCallbackProperty(
        positions,
        origin,
        entity.position,
        oldOrientation,
        entity.orientation,
        oldValue,
        entity.scale
      );
    } else {
      const newReactangle = transformRectangle(
        positions,
        origin,
        origin,
        oldOrientation,
        oldOrientation,
        oldValue,
        newValue
      );
      rectangle._coordinates = new ConstantProperty(newReactangle);
    }
    rectangle.definitionChanged.raiseEvent(
      entity,
      "rectangle",
      rectangle,
      rectangle
    );
  }

  // tileset => computeModelMatirx

  // wall
  const wall = entity._wall;
  if (defined(wall)) {
    const positions = getPropertyValue(wall.positions);
    if (isCallback) {
      wall._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        oldOrientation,
        entity.orientation,
        oldValue,
        entity.scale
      );
    } else {
      const newPositions = transformGeometry(
        positions,
        origin,
        origin,
        oldOrientation,
        oldOrientation,
        oldValue,
        newValue
      );
      wall._positions = new ConstantProperty(newPositions);
    }
    wall.definitionChanged.raiseEvent(entity, "wall", wall, wall);
  }
};

/**
 * Entity.position 변경 이벤트 핸들러
 *
 * @private
 * @param {DitapEntity} entity
 * @param {Property | undefined} oldValue
 * @param {Property | undefined} newValue
 * @param {JulianDate} time
 */
const positionChangedHandler = (entity, newValue, oldValue, time) => {
  try {
    const origin = getPropertyValue(entity.position);
    const _newValue = getPropertyValue(newValue, time) || origin;
    const _oldValue = getPropertyValue(oldValue, time) || origin;

    if (defined(origin)) {
      setEntityPosition(entity, _oldValue, _newValue);
    } else {
      throw Error("Entity position property is undefined");
    }
  } catch (error) {
    throw Error(error);
  }
};

/**
 * Entity.orientation 변경 이벤트 핸들러
 *
 * @private
 * @param {DitapEntity} entity
 * @param {Property | undefined} oldValue
 * @param {Property | undefined} newValue
 * @param {JulianDate} time
 */
const orientationChangedHandler = (entity, newValue, oldValue, time) => {
  try {
    const origin = getEntityPosition(entity);
    const _oldValue =
      getPropertyValue(oldValue, time) ||
      Transforms.headingPitchRollQuaternion(
        origin,
        new HeadingPitchRoll(0, 0, 0)
      );
    const _newValue =
      getPropertyValue(newValue, time) ||
      Transforms.headingPitchRollQuaternion(
        origin,
        new HeadingPitchRoll(0, 0, 0)
      );

    if (defined(origin)) {
      // 회전 적용
      setEntityRotation(entity, origin, _newValue, _oldValue);
    } else {
      throw Error("entity position is undefined");
    }
  } catch (error) {
    throw Error(error);
  }
};

/**
 * Entity.scale 변경 이벤트 핸들러
 *
 * @private
 * @param {DitapEntity} entity
 * @param {Property | undefined} oldValue
 * @param {Property | undefined} newValue
 * @param {JulianDate} time
 */
const scaleChangedHandler = (entity, newValue, oldValue, time) => {
  try {
    const _oldValue =
      getPropertyValue(oldValue, time) || new Cartesian3(1, 1, 1);
    const _newValue =
      getPropertyValue(newValue, time) || new Cartesian3(1, 1, 1);
    const origin = getEntityPosition(entity);

    if (defined(origin)) {
      // 스케일 적용
      setEntityScale(entity, origin, _newValue, _oldValue);
    } else {
      throw Error("entity position is undefined");
    }
  } catch (error) {
    throw Error(error);
  }
};

/**
 * Entity Geometry Updator
 * @private
 * @param {DitapEntity} entity
 * @param {String} property
 * @param {Object} newValue
 * @param {Object} oldValue
 * @param {JulianDate} time
 */
const geometryUpdator = (entity, property, newValue, oldValue, time) => {
  const isCallback = checkIsCallback(entity);
  const origin = getPropertyValue(entity.position, time);
  if (!defined(origin) || !(origin instanceof Cartesian3)) {
    throw Error("Entity 'position' property is invalid");
  }
  const orientation =
    getPropertyValue(entity.orientation, time) ||
    Transforms.headingPitchRollQuaternion(
      origin,
      new HeadingPitchRoll(0, 0, 0)
    );
  const scale = getPropertyValue(entity.scale, time) || SCALE_IDENTITY;

  if (property === "polygon") {
    if (oldValue.positions !== newValue.positions && isCallback) {
      const positions = getPropertyValue(newValue.hierarchy);
      entity._polygon._hierarchy = createHierarchyCallbackProperty(
        positions,
        origin,
        entity.position,
        orientation,
        entity.orientation,
        scale,
        entity.scale
      );
    }
  } else if (property === "polyline") {
    if (oldValue.positions !== newValue.positions && isCallback) {
      const positions = getPropertyValue(newValue.positions);
      entity._polyline._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        orientation,
        entity.orientation,
        scale,
        entity.scale
      );
    }
  } else if (property === "polylineVolume") {
    // positions
    const positions = getPropertyValue(newValue.positions);
    entity._polylineVolume._positions = createGeometryCallbackProperty(
      positions,
      origin,
      entity.position,
      orientation,
      entity.orientation,
      scale,
      entity.scale
    );
  } else if (property === "rectangle") {
    if (oldValue.positions !== newValue.positions && isCallback) {
      const positions = getPropertyValue(newValue.coordinates);
      entity._rectangle._coordinates = createRectangleCallbackProperty(
        positions,
        origin,
        entity.position,
        orientation,
        entity.orientation,
        scale,
        entity.scale
      );
    }
  } else if (property === "wall") {
    if (oldValue.positions !== newValue.positions && isCallback) {
      const positions = getPropertyValue(newValue.positions);
      entity._wall._positions = createGeometryCallbackProperty(
        positions,
        origin,
        entity.position,
        orientation,
        entity.orientation,
        scale,
        entity.scale
      );
    }
  }
};

/**
 * Entity 프로퍼티 값 변경 이벤트 핸들러
 *
 * @private
 * @param {DitapEntity} entity
 * @param {String} property
 * @param {*} oldValue
 * @param {*} newValue
 */
const definitionChangedHandler = (entity, property, newValue, oldValue) => {
  const time = new JulianDate();

  // Entity.position
  if (property === "position") {
    positionChangedHandler(entity, newValue, oldValue, time);
    // Entity.orientation
  } else if (property === "orientation") {
    orientationChangedHandler(entity, newValue, oldValue, time);
    // Entity.scale
  } else if (property === "scale") {
    scaleChangedHandler(entity, newValue, oldValue, time);
  } else {
    if (newValue !== oldValue) {
      return;
    }

    geometryUpdator(entity, property, newValue, oldValue, time);
  }
};

/**
 * Ditap 전용 Entity 클래스. <br>
 * Ditap-specific Entity class. <br>
 * Note: model, tileset은 로컬 좌표계 기준으로 동작. <br>
 * Note: Model and tileset operate based on the local coordinate system. <br>
 *
 * @alias DitapEntity
 * @extends Entity
 * @property {string} [id] 이 개체의 고유 식별자입니다. 아무것도 제공되지 않으면 GUID가 생성됩니다.<br> A unique identifier for this object. If none is provided, a GUID is generated.
 * @property {string} [name] 사용자에게 표시할 사람이 읽을 수 있는 이름입니다. 고유할 필요는 없습니다. <br> A human readable name to display to users. It does not have to be unique.
 * @property {TimeIntervalCollection} [availability] <br> The availability, if any, associated with this object.
 * @property {boolean} [show] 이 객체와 연관된 가용성(있는 경우)입니다. <br> A boolean value indicating if the entity and its children are displayed.
 * @property {Property | string} [description] 이 엔터티에 대한 HTML 설명을 지정하는 문자열 속성입니다. <br> A string Property specifying an HTML description for this entity.
 * @property {PositionProperty | Cartesian3} [position] 엔터티 위치를 지정하는 속성입니다. <br> A Property specifying the entity position.
 * @property {Property} [orientation] 엔터티 방향을 지정하는 속성입니다. <br> A Property specifying the entity orientation.
 * @property {Property} [scale] 엔터티 규모를 지정하는 Scale입니다. <br> A Scale specifying the entity scale.
 * @property {Property} [viewFrom] 이 객체를 보기 위한 제안된 초기 오프셋입니다. <br> A suggested initial offset for viewing this object.
 * @property {Entity} [parent] 이 엔터티와 연결할 상위 엔터티입니다. <br> A parent entity to associate with this entity.
 * @property {BillboardGraphics | BillboardGraphics.ConstructorOptions} [billboard] 이 엔터티와 연결할 빌보드입니다. <br> A billboard to associate with this entity.
 * @property {BoxGraphics | BoxGraphics.ConstructorOptions} [box] 이 엔터티와 연결할 박스입니다. <br> A box to associate with this entity.
 * @property {CorridorGraphics | CorridorGraphics.ConstructorOptions} [corridor] 이 엔터티와 연결할 corridor입니다. <br> A corridor to associate with this entity.
 * @property {CylinderGraphics | CylinderGraphics.ConstructorOptions} [cylinder] 이 엔터티와 연결할 원통입니다. <br> A cylinder to associate with this entity.
 * @property {EllipseGraphics | EllipseGraphics.ConstructorOptions} [ellipse] 이 엔터티와 연결할 타원입니다. <br> A ellipse to associate with this entity.
 * @property {EllipsoidGraphics | EllipsoidGraphics.ConstructorOptions} [ellipsoid] 이 엔터티와 연결할 타원체입니다. <br> A ellipsoid to associate with this entity.
 * @property {LabelGraphics | LabelGraphics.ConstructorOptions} [label] 이 엔터티와 연결할 라벨입니다. <br> A label to associate with this entity.
 * @property {ModelGraphics | ModelGraphics.ConstructorOptions} [model] 이 엔터티와 연결할 모델입니다. <br> A model to associate with this entity.
 * @property {Cesium3DTilesetGraphics | Cesium3DTilesetGraphics.ConstructorOptions} [tileset] 이 개체와 연결할 3D 타일 세트입니다. <br> A 3D Tiles tileset to associate with this entity.
 * @property {PathGraphics | PathGraphics.ConstructorOptions} [path] 이 엔터티와 연결할 경로입니다. <br> A path to associate with this entity.
 * @property {PlaneGraphics | PlaneGraphics.ConstructorOptions} [plane] 이 엔터티와 연결할 평면입니다. <br> A plane to associate with this entity.
 * @property {PointGraphics | PointGraphics.ConstructorOptions} [point] 이 엔터티와 연결할 지점입니다. <br> A point to associate with this entity.
 * @property {PolygonGraphics | PolygonGraphics.ConstructorOptions} [polygon] 이 엔터티와 연결할 다각형입니다. <br> A polygon to associate with this entity.
 * @property {PolylineGraphics | PolylineGraphics.ConstructorOptions} [polyline] 이 엔터티와 연결할 폴리라인입니다. <br> A polyline to associate with this entity.
 * @property {PropertyBag | Object<string,*>} [properties] 이 엔터티와 연결할 임의 속성입니다. <br> Arbitrary properties to associate with this entity.
 * @property {PolylineVolumeGraphics | PolylineVolumeGraphics.ConstructorOptions} [polylineVolume] 이 엔터티와 연결할 폴리라인 볼륨입니다. <br> A polylineVolume to associate with this entity.
 * @property {RectangleGraphics | RectangleGraphics.ConstructorOptions} [rectangle] 이 엔터티와 연결할 직사각형입니다. <br> A rectangle to associate with this entity.
 * @property {WallGraphics | WallGraphics.ConstructorOptions} [wall] 이 개체와 연결할 벽입니다. <br> A wall to associate with this entity.
 */
class DitapEntity extends Entity {
  constructor(options) {
    super(options);

    // position 기본값 세팅
    if (defined(this.position)) {
      positionChangedHandler(
        this,
        this.position,
        this.position,
        new JulianDate()
      );
    } else if (!defined(this.position)) {
      this._position = new ConstantPositionProperty(getEntityPosition(this));
    }

    // DitapEntity 전용 속성
    this._scale = undefined;
    this._scaleSubscription = undefined;
    this._propertyNames = [
      "billboard",
      "box",
      "corridor",
      "cylinder",
      "description",
      "ellipse", //
      "ellipsoid",
      "label",
      "model",
      "tileset",
      "orientation",
      "path",
      "plane",
      "point",
      "polygon", //
      "polyline",
      "polylineVolume",
      "position",
      "properties",
      "rectangle",
      "viewFrom",
      "wall",
      "scale",
    ];
    Object.defineProperties(this, {
      /**
       * Gets or sets the scale.
       * @memberof Entity.prototype
       * @type {Property|undefined}
       */
      scale: createPropertyDescriptor("scale"),
    });

    // orientation 초기값이 있을 경우 raiseEvent
    if (defined(this.orientation) && defined(this._position)) {
      const positionOffset = Utils.getPropertyValue(this._position);
      if (positionOffset) {
        const orientationOffset = Transforms.headingPitchRollQuaternion(
          positionOffset,
          new HeadingPitchRoll(0, 0, 0)
        );
        orientationChangedHandler(
          this,
          this.orientation,
          orientationOffset,
          new JulianDate()
        );
      }
    }

    // 초기 입력 값이 있을 경우 세팅
    if (options.scale) {
      this._scale = options.scale;
      scaleChangedHandler(
        this,
        options.scale,
        SCALE_IDENTITY,
        new JulianDate()
      );
    }

    // definitionChangedHandler 추가
    this._definitionChanged.addEventListener(definitionChangedHandler);
  }

  /**
   * Computes the model matrix for the entity's transform at specified time. Returns undefined if position is undefined
   *
   * @param {JulianDate} time The time to retrieve model matrix for.
   * @param {Matrix4} [result] The object onto which to store the result.
   *
   * @returns {Matrix4} The modified result parameter or a new Matrix4 instance if one was not provided. Result is undefined if position is undefined.
   */
  computeModelMatrix(time, result) {
    return ditapEntityComputeModelMatrix(this, time, result);
  }

  // /**
  //  * @private
  //  */
  // computeModelMatrixForHeightReference(
  //   time,
  //   heightReferenceProperty,
  //   heightOffset,
  //   ellipsoid,
  //   result
  // ) {
  //   return ditapEntityComputeModelMatrixForHeightReference(
  //     this,
  //     time,
  //     heightReferenceProperty,
  //     heightOffset,
  //     ellipsoid,
  //     result
  //   );
  // }
}

export default DitapEntity;
